/**
 * NPM Reds — two-level approval for Miscellaneous Tasks (TAS-358).
 *
 * These mirror the API contract at `/admin/miscellaneousTasks` (TAS-355/356)
 * field-for-field. They describe the QUEUE-ENVELOPE shape only: the same route
 * still returns a legacy bare array when `queue` is omitted, which the v1 page
 * consumes through `interfaces/MiscEmployeeTaskInterface`. Nothing here is
 * shared with that shape — the two are deliberately separate.
 */

/** Which bucket the caller is asking for. Always sent; omitting it downgrades the
 *  response to the legacy array, which this feature can never render. */
export type MiscTaskQueue = 'firstLevel' | 'secondLevel' | 'approved' | 'denied';

/** 1 FirstPending · 2 SecondPending · 3 Approved · 4 Denied. */
export const MISC_TASK_STATUS = {
  firstPending: 1,
  secondPending: 2,
  approved: 3,
  denied: 4,
} as const;

/** Every action the audit trail can record. */
export type MiscTaskHistoryAction =
  | 'APPROVE'
  | 'FORCE_APPROVE'
  | 'DENY'
  | 'REOPEN'
  | 'DESCRIPTION_EDIT';

export interface MiscTaskPersonName {
  first: string;
  /** The API sends "" rather than null, but tolerate both. */
  middle: string | null;
  last: string;
}

export interface MiscTaskSupervisor {
  employeeID: number;
  altEmployeeID: string;
  name: MiscTaskPersonName;
}

export interface MiscTaskProject {
  projectID: number;
  altProjectID: string;
  name: string;
}

/**
 * One outstanding approval slot. Either an individual (`employeeID` + `name`) or
 * a group (`approvalGroupID` + `approvalGroupName`) — never both. `name` is a
 * pre-joined string, not a name object.
 */
export interface MiscTaskPendingApprover {
  employeeID: number | null;
  name: string | null;
  approvalGroupID: number | null;
  approvalGroupName: string | null;
}

/** The most recent audit row, denormalised onto the row so the list needs no
 *  history call to explain a reopen. */
export interface MiscTaskLastAction {
  action: MiscTaskHistoryAction | string;
  level: number;
  actionDateTime: string;
  /** The employee's name when the actor was the employee (a mobile edit / REOPEN). */
  adminName: string;
  comments: string | null;
}

export interface MiscTaskRow {
  miscEmployeeTaskID: number;
  employeeID: number;
  altEmployeeID: string;
  name: MiscTaskPersonName;
  /** null when the employee has no supervisor on record. */
  supervisor: MiscTaskSupervisor | null;
  project: MiscTaskProject;
  intermediateID: number | null;
  altIntermediateID: string | null;
  /** Phase path joined with " > ". Empty string when the red has no phase. */
  intermediateName: string;
  /**
   * The WORK date. NPM records reds week-by-week, so every red in a pay period
   * carries the same period-start date — it is what reaches Vista and payroll,
   * and it is worth nothing as a column. Shown as "Work date" in the detail.
   */
  entryDate: string;
  /**
   * When the red was submitted — ALREADY OFFSET-COMPOSED LOCAL TIME, NOT UTC.
   *
   * 'YYYY-MM-DDTHH:mm:ss' with NO zone marker. The API applied
   * SWITCHOFFSET(InsertDateTime, InsertDateTimeOffset) in SQL, so these digits
   * are the submitter's own wall clock. Render them as they stand — via
   * `formatSubmittedDay` / `formatSubmittedStamp`, which read the digits
   * lexically. NEVER pass this to Date.parse-and-localise, and never re-apply
   * `submittedDateTimeOffset`: either would shift an evening submission a day.
   */
  submittedDateTime: string | null;
  /** The zone the stamp was taken in ('-07:00'). Already baked in — a label, not an adjustment. */
  submittedDateTimeOffset: string | null;
  amountClaimed: number;
  /** Set only once a deny has zeroed `amountClaimed`. */
  originalAmountClaimed: number | null;
  miscDescription: string | null;
  superDescription: string | null;
  /** 1 | 2 | 3 | 4 — see MISC_TASK_STATUS. */
  approvalStatus: number;
  pendingLevel: number | null;
  /**
   * Computed per caller by the server: true means an approve at `pendingLevel`
   * will neither 403 nor 409. Trust it — the client cannot re-derive routing.
   */
  actionable: boolean;
  /**
   * PER-CALLER (TAS-543). Set when the caller's only hold on an unsatisfied slot
   * at `pendingLevel` is a SUPERVISOR_TREE approver delegation — the slot's real
   * approver, whom they are covering for. Null when they hold a slot at that
   * level directly (own or group) or hold none. Drives the "You, for <name>"
   * badge; `pendingApprovers` still lists the real approver.
   */
  actingFor: MiscTaskPendingApprover | null;
  pendingSince: string | null;
  pendingApprovers: MiscTaskPendingApprover[];
  /** 1 + the number of REOPENs. >1 means the chain restarted after an edit. */
  generation: number;
  denyReason: string | null;
  deniedByAdminEmployeeID: number | null;
  deniedByName: string | null;
  deniedDateTime: string | null;
  lastAction: MiscTaskLastAction | null;
}

/**
 * Who is looking. Drives the "Acting as" header, the result-line scope text and
 * the two oversight capabilities the row payload cannot express.
 */
export interface MiscTaskViewer {
  adminEmployeeID: number;
  name: string;
  /** e.g. "every location", "Corona + dept 4", "your reds only". */
  scopeLabel: string;
  canForceApprove: boolean;
  canOversee: boolean;
}

export interface MiscTaskQueueResponse {
  viewer: MiscTaskViewer;
  rows: MiscTaskRow[];
}

/**
 * Stage-card numbers. `yours` is the big number, `period` the sub-line, `dollars`
 * the bucket sum. Server-scoped — a caller who sees fewer rows than exist is the
 * normal case, so none of these may be recomputed from the loaded rows.
 */
export interface MiscTaskBucketCount {
  yours: number;
  period: number;
  dollars: number;
}

export interface MiscTaskCounts {
  firstLevel: MiscTaskBucketCount;
  secondLevel: MiscTaskBucketCount;
  approved: MiscTaskBucketCount;
  denied: MiscTaskBucketCount;
  /** Visible pending reds that routed to nobody — drives the no-approver line. */
  unroutable: number;
}

/**
 * One audit-trail entry, newest first.
 *
 * `adminEmployeeID` is null on a REOPEN the employee performed from mobile; the
 * server already resolves `actorName` to the employee in that case and flags it
 * with `byEmployee`, so a null admin is never a missing admin.
 *
 * The trail carries no `generation` and no previous-description field — both are
 * derived client-side in miscTaskHelpers.
 */
export interface MiscTaskHistoryEntry {
  miscEmployeeTaskApprovalID: number;
  action: MiscTaskHistoryAction | string;
  approvalLevel: number | null;
  actionDateTime: string;
  actionDateTimeOffset: string | null;
  adminEmployeeID: number | null;
  actorName: string;
  byEmployee: boolean;
  comments: string | null;
  superDescriptionSnapshot: string | null;
}

/** Every mutation answers with this shape on 200. */
export interface MiscTaskActionResult {
  success: boolean;
  approvalStatus?: number;
  errorCode?: string;
  message?: string;
}

export interface MiscTaskApproveRequest {
  level: 1 | 2;
  superDescription?: string;
  comments?: string;
}
